import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, ParallaxImage, SectionLabel } from "./common";
import { resolveMedia } from "../../lib/api";
import { DEFAULT_SERVICES } from "../../constants/defaultSiteData";

const slugMap = {
  "BRAND ENGINEERING": "brand-engineering",
  "MULTIMEDIA PRODUCTION": "multimedia-production",
  "DIGITAL MARKETING": "digital-marketing",
  "PERFORMANCE & LEAD GENERATION": "performance-lead-generation",
  "AI-POWERED MARKETING": "ai-powered-marketing",
};

/* CAPABILITIES */
export function Build({ data }) {
  const d = data || {};
  const items = d.items || [];
  return (
    <section id="build" data-testid="section-build" className="bg-white py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>Capabilities</SectionLabel>
        <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-navy sm:text-6xl">
          {d.heading}
        </h2>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div className="group relative h-[360px] overflow-hidden rounded-2xl" data-testid={`build-item-${i}`}>
                <img src={resolveMedia(it.image)} alt={it.title} loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white">{it.title}</h3>
                  <p className="mt-2 max-h-0 overflow-hidden font-body text-sm text-white/80 opacity-0 transition-all duration-500 group-hover:max-h-28 group-hover:opacity-100">
                    {it.desc}
                  </p>
                </div>
                <span className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition group-hover:bg-[#FF5722]">
                  <ArrowUpRight size={18} />
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* CONNECTED MARKETING SYSTEM */
export function System({ data }) {
  const d = data || {};
  return (
    <section id="system" data-testid="section-system" className="bg-[#121622] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel dark>The System</SectionLabel>
        <div className="grid gap-14 lg:grid-cols-2">
          <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
            {(d.heading_lines || []).map((l, i) => (
              <span key={i} className={`block ${i === (d.heading_lines || []).length - 1 ? "text-[#FF5722]" : "text-white"}`}>{l}</span>
            ))}
          </h2>
          <div>
            <div className="space-y-0">
              {(d.points || []).map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex items-baseline gap-4 border-b border-white/10 py-4">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#FF5722]" />
                    <p className="font-body text-lg text-white/80 sm:text-xl">
                      <span className="font-semibold text-white">{p.k}</span> {p.v}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-8 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{d.conclusion}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* SERVICES */
export function Services({ services }) {
  const [open, setOpen] = useState(0);
  const list = (services && services.length > 0) ? services : DEFAULT_SERVICES;
  return (
    <section id="services" data-testid="section-services" className="bg-[#F0F2F5] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-14">
          <div>
            <SectionLabel>Services</SectionLabel>
            <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-navy sm:text-6xl">
              What ADMALL Delivers
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:underline"
          >
            All Services Overview <ArrowRight size={16} />
          </Link>
        </div>

        <div className="space-y-4">
          {list.map((s, i) => {
            const active = open === i;
            const targetSlug = slugMap[s.title] || s.title?.toLowerCase().replace(/[^a-z0-9]+/g, "-");
            return (
              <div key={s.id || i} data-testid={`service-panel-${i}`}
                className={`overflow-hidden rounded-2xl border transition-all ${active ? "border-[#FF5722] bg-navy" : "border-navy/10 bg-white"}`}>
                <button
                  data-testid={`service-toggle-${i}`}
                  onClick={() => setOpen(active ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left md:px-10 md:py-8"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="h-2 w-2 rounded-full bg-[#FF5722] shrink-0" />
                    <h3 className={`font-display text-2xl font-extrabold uppercase tracking-tight sm:text-4xl ${active ? "text-white" : "text-navy"}`}>{s.title}</h3>
                  </div>
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${active ? "bg-[#FF5722] text-white" : "bg-navy/5 text-navy"}`}>
                    {active ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                <div className={`grid transition-all duration-500 ${active ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <div className="grid gap-8 px-6 pb-8 md:grid-cols-2 md:px-10 md:pb-10">
                      <div>
                        <p className="mb-5 font-display text-lg font-bold uppercase tracking-wide text-[#FF5722]">{s.tagline}</p>
                        <div className="flex flex-wrap gap-2">
                          {(s.bullets || []).map((b, bi) => (
                            <span key={bi} className="rounded-full border border-white/15 px-3 py-1.5 font-body text-xs text-white/80">{b}</span>
                          ))}
                        </div>
                        <div className="mt-6">
                          <Link
                            to={`/services/${targetSlug}`}
                            className="inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-[#E64A19] transition"
                          >
                            Explore Dedicated {s.title} Page <ArrowRight size={14} />
                          </Link>
                        </div>
                      </div>
                      <div className="overflow-hidden rounded-xl">
                        <img src={resolveMedia(s.image)} alt={s.title} loading="lazy" className="aspect-[16/10] w-full object-cover" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* MULTIMEDIA STORYTELLING */
export function Storytelling({ data }) {
  const d = data || {};
  const imgs = d.images || [];
  return (
    <section id="storytelling" data-testid="section-storytelling" className="relative overflow-hidden bg-navy py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel dark>Multimedia</SectionLabel>
        <h2 className="max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
          {d.heading}
        </h2>
        <div className="mt-14 grid gap-4 md:grid-cols-12">
          {imgs[0] && <ParallaxImage src={imgs[0]} className="aspect-[4/5] md:col-span-5" speed={0.1} testId="story-img-0" overlay />}
          <div className="grid gap-4 md:col-span-7">
            {imgs[1] && <ParallaxImage src={imgs[1]} className="aspect-[16/9]" speed={0.14} testId="story-img-1" overlay />}
            <div className="grid grid-cols-2 gap-4">
              {imgs[2] && <ParallaxImage src={imgs[2]} className="aspect-square" speed={0.12} testId="story-img-2" overlay />}
              <div className="flex flex-col justify-center rounded-2xl bg-[#FF5722] p-6">
                <p className="font-display text-2xl font-extrabold uppercase leading-tight text-white sm:text-3xl">{d.quote}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
